import React from 'react';
import { CheckCircle2, Menu, X, Shield, Clock, BarChart3, Smartphone, Camera, Cloud, Bell, Users, FileText } from 'lucide-react';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Navigation Header */}
      <nav className="nav-header">
        <div className="flex items-center gap-2">
          <div className="text-xl font-bold" style={{ color: 'var(--text-primary)', padding: '0 0.5rem' }}>SOP360</div>
        </div>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          <button onClick={() => scrollToSection('features')} className="nav-link">Features</button>
          <button onClick={() => scrollToSection('industries')} className="nav-link">Industries</button>
          <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
        </div>
        
        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          style={{ color: 'var(--text-primary)' }}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed top-20 left-4 right-4 bg-white border border-gray-200 rounded-2xl shadow-lg z-40 md:hidden">
          <div className="flex flex-col p-4 gap-2">
            <button onClick={() => scrollToSection('features')} className="nav-link text-left">Features</button>
            <button onClick={() => scrollToSection('industries')} className="nav-link text-left">Industries</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link text-left">Contact</button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section" id="home">
        <div className="hero-content">
          <h1 className="heading-1">SOP360</h1>
          <p className="body-large" style={{ color: 'var(--accent-text)', marginBottom: '0.5rem' }}>
            Simple tool to run your business smoothly, every day
          </p>
          <h2 className="heading-2" style={{ marginTop: '1.5rem' }}>Run your outlets smoothly, with confidence.</h2>
          <p className="body-large" style={{ marginBottom: '2rem' }}>
            A mobile-first daily operations app that helps shops, outlets and small factories record important checks, 
            maintain logs, and stay compliant — without paper or complex software.
          </p>
          <div className="flex flex-wrap gap-4 justify-center items-center mb-6">
            <span className="px-4 py-2 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)', fontWeight: 600 }}>Mobile App</span>
            <span className="px-4 py-2 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)', fontWeight: 600 }}>SaaS</span>
            <span className="px-4 py-2 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)', fontWeight: 600 }}>Offline-first</span>
            <span className="px-4 py-2 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)', fontWeight: 600 }}>AI Insights</span>
          </div>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <button onClick={() => scrollToSection('contact')} className="btn-primary">Get Started</button>
            <button onClick={() => scrollToSection('features')} className="btn-secondary">Learn More</button>
          </div>
          
          <div className="mt-12">
            <img 
              src="https://images.unsplash.com/photo-1590488357944-a71ec5581dfe" 
              alt="Person using mobile operations app" 
              className="rounded-2xl shadow-lg w-full max-w-2xl mx-auto"
              style={{ maxHeight: '500px', objectFit: 'cover' }}
            />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="pad-2xl" id="problem" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <h2 className="heading-2 text-center mb-12">The Real-World Problem</h2>
          <div className="max-w-3xl mx-auto">
            <p className="body-large text-center mb-8">
              Running daily operations sounds simple. But on the ground, many small businesses face the same pain every day:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Work is done — but no proof.',
                'Logbooks exist — but are not updated correctly.',
                'Staff forget important steps during busy hours.',
                'No visibility of which outlet followed SOP and which did not.',
                'When audit or inspection comes — papers are missing.',
                'Owners have to depend on manual trust rather than data.'
              ].map((problem, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <CheckCircle2 size={24} style={{ color: 'var(--accent-strong)', flexShrink: 0 }} />
                  <p className="body-medium">{problem}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 p-6 rounded-2xl" style={{ background: 'var(--accent-wash)', borderLeft: '4px solid var(--accent-strong)' }}>
              <p className="body-medium">
                Paper checklists get lost. WhatsApp photos are not organized. Important readings are not stored properly.
              </p>
              <p className="body-medium font-semibold mt-4" style={{ color: 'var(--accent-text)' }}>
                SOP360 solves this by making data collection simple, quick and reliable, directly from a mobile phone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What SOP360 Does */}
      <section className="pad-2xl" id="features">
        <div className="container">
          <h2 className="heading-2 text-center mb-4">What SOP360 Does</h2>
          <p className="body-large text-center mb-12" style={{ color: 'var(--text-secondary)' }}>
            SOP360 brings all your daily operations, checklists and records into one simple mobile app.
          </p>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="heading-3 mb-6">With SOP360, your team can:</h3>
              <div className="space-y-4">
                {[
                  { icon: <FileText size={20} />, text: 'Follow checklists digitally' },
                  { icon: <Camera size={20} />, text: 'Capture photos and readings as proof' },
                  { icon: <Clock size={20} />, text: 'Complete tasks on schedule (hourly / daily / shift-based)' },
                  { icon: <BarChart3 size={20} />, text: 'Record attendance, stock and meter readings' },
                  { icon: <Shield size={20} />, text: 'Create incident logs if something goes wrong' },
                  { icon: <FileText size={20} />, text: 'Auto-generate reports from the collected data' }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-center">
                    <div className="p-2 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
                      {item.icon}
                    </div>
                    <p className="body-medium">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1521579498714-ff08ba4836ab" 
                alt="Mobile app interface" 
                className="rounded-2xl shadow-lg w-full"
                style={{ maxHeight: '500px', objectFit: 'cover' }}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="https://images.unsplash.com/photo-1713989765634-5d357b49303c" 
                alt="Manager dashboard" 
                className="rounded-2xl shadow-lg w-full"
                style={{ maxHeight: '500px', objectFit: 'cover' }}
              />
            </div>
            <div className="order-1 md:order-2">
              <h3 className="heading-3 mb-6">As the owner/manager, you can:</h3>
              <div className="space-y-4">
                {[
                  { icon: <BarChart3 size={20} />, text: 'See what is done and what is missed' },
                  { icon: <Users size={20} />, text: 'Compare multiple outlets/locations' },
                  { icon: <Bell size={20} />, text: 'Get alerts when there is a deviation' },
                  { icon: <Cloud size={20} />, text: 'View past records any time, with proof' }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-center">
                    <div className="p-2 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
                      {item.icon}
                    </div>
                    <p className="body-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="body-medium mt-6 font-semibold" style={{ color: 'var(--accent-text)' }}>
                Everything is saved securely and stays audit-ready.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="pad-2xl" id="industries" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <h2 className="heading-2 text-center mb-12">Trusted Across Industries</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Dairy */}
            <div className="product-card">
              <img 
                src="https://images.unsplash.com/photo-1580686954168-b08d0309d203" 
                alt="Dairy processing facility" 
                className="rounded-lg mb-4 w-full h-48 object-cover"
              />
              <h3 className="heading-3">Dairy / Milk Processing</h3>
              <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
                Dairy operations require hygiene logs, cold chain checks and process discipline.
              </p>
              <div className="space-y-2">
                <p className="body-small">✓ Milk receiving temperature</p>
                <p className="body-small">✓ Chiller / cold room temperature logs</p>
                <p className="body-small">✓ Pasteurization batch records</p>
                <p className="body-small">✓ CIP / sanitation checklists</p>
                <p className="body-small">✓ FSSAI compliance ready</p>
              </div>
            </div>

            {/* Fuel Stations */}
            <div className="product-card">
              <img 
                src="https://images.unsplash.com/photo-1559110603-84f34d979679" 
                alt="Fuel station operations" 
                className="rounded-lg mb-4 w-full h-48 object-cover"
              />
              <h3 className="heading-3">Fuel Stations / Petrol Pumps</h3>
              <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
                Fuel outlets must maintain accurate records every day.
              </p>
              <div className="space-y-2">
                <p className="body-small">✓ Opening stock reading per nozzle</p>
                <p className="body-small">✓ Density and temperature reading</p>
                <p className="body-small">✓ Seal / safety check with photo proof</p>
                <p className="body-small">✓ Water contamination check</p>
                <p className="body-small">✓ Daily reconciliation reports</p>
              </div>
            </div>

            {/* Food Outlets */}
            <div className="product-card">
              <img 
                src="https://images.unsplash.com/photo-1588416820614-f8d6ac6cea56" 
                alt="Restaurant kitchen" 
                className="rounded-lg mb-4 w-full h-48 object-cover"
              />
              <h3 className="heading-3">Food Outlets / Restaurants</h3>
              <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
                Food outlets need discipline in hygiene, cleaning and food safety.
              </p>
              <div className="space-y-2">
                <p className="body-small">✓ Opening & closing checklists</p>
                <p className="body-small">✓ Kitchen hygiene & sanitation</p>
                <p className="body-small">✓ Fryer / oil temperature logs</p>
                <p className="body-small">✓ Food storage & expiry checks</p>
                <p className="body-small">✓ Standard process across outlets</p>
              </div>
            </div>

            {/* Hospitals */}
            <div className="product-card">
              <img 
                src="https://images.unsplash.com/photo-1613377512507-92803ec4ef17" 
                alt="Hospital environment" 
                className="rounded-lg mb-4 w-full h-48 object-cover"
              />
              <h3 className="heading-3">Hospitals & Clinics</h3>
              <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
                Healthcare needs proper process documentation for quality and safety.
              </p>
              <div className="space-y-2">
                <p className="body-small">✓ OT cleaning & sanitization logs</p>
                <p className="body-small">✓ Patient-area hygiene checks</p>
                <p className="body-small">✓ Cold storage temperature monitoring</p>
                <p className="body-small">✓ Biomedical waste disposal tracking</p>
                <p className="body-small">✓ NABH compliance support</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features + AI */}
      <section className="pad-2xl">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div>
              <h3 className="heading-3 mb-6">Key Features</h3>
              <div className="space-y-3">
                {[
                  'One-click checklists',
                  'Photos & evidence capture',
                  'Works offline',
                  'Role-wise mobile interface',
                  'Shift-wise tracking',
                  'Multi-branch support',
                  'Attendance & basic ledger',
                  'Incident logging & follow-up',
                  'Auto-generated reports'
                ].map((feature, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <CheckCircle2 size={18} style={{ color: 'var(--accent-strong)' }} />
                    <p className="body-medium">{feature}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="heading-3 mb-6">AI Assistance</h3>
              <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
                Built-in AI helps you:
              </p>
              <div className="space-y-3">
                {[
                  'Detect repeated failures',
                  'Identify missing steps or delays',
                  'Suggest process improvements',
                  'Highlight risky patterns early'
                ].map((feature, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-strong)' }} />
                    <p className="body-medium">{feature}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="heading-3 mb-6">Why Owners Love SOP360</h3>
              <div className="space-y-3">
                {[
                  'Simple for staff to use',
                  'Full clarity on ground operations',
                  'Saves time during audits',
                  'No paperwork to maintain',
                  'Helps run outlets smoothly'
                ].map((benefit, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-strong)' }} />
                    <p className="body-medium">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center p-12 rounded-3xl" style={{ background: 'var(--gradient-hero)' }}>
            <h2 className="heading-2 mb-4">Turn your daily operations into reliable, trackable records</h2>
            <p className="body-large mb-8">All from your mobile.</p>
            <button onClick={() => scrollToSection('contact')} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pad-2xl" id="contact" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-2 mb-4">Get Started with SOP360</h2>
            <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
              Ready to transform your daily operations? Contact us to learn more or schedule a demo.
            </p>
            <a 
              href="mailto:sop360@spiceedge.in" 
              className="btn-primary inline-flex items-center gap-2"
            >
              Contact Us
            </a>
            <p className="body-small mt-4" style={{ color: 'var(--text-muted)' }}>
              Email: sop360@spiceedge.in
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pad-lg" style={{ borderTop: '1px solid var(--border-light)' }}>
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>SOP360</div>
            <p className="caption">© 2025 SOP360. Simple tool to run your business smoothly, every day.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;